import React from 'react';
import { X } from 'lucide-react';
import styled from 'styled-components';
import { Modal } from '../Modal';
import {
  PreviewContainer,
  PreviewImage,
  PreviewDocument,
  DocumentIcon,
  DocumentText,
  PreviewFileName,
  CloseButton,
} from './FileUpload.styled';
import { FileUploadState } from './FileManager';
import { useImageWithFallback } from '../../../hooks/useImageWithFallback';

const PreviewCloseButton = styled(CloseButton)`
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
`;

const PDFPreview = styled.iframe`
  width: 100%;
  height: 70vh;
  border: none;
  border-radius: var(--radius-md);
`;

const VideoPreview = styled.video`
  max-width: 100%;
  max-height: 50vh;
  border-radius: var(--radius-md);
  background: #000;
`;

const ViewFileButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--color-primary-600);
  color: var(--color-white);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-14);
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: var(--color-primary-700);
  }

  &:disabled {
    background: var(--color-gray-300);
    cursor: not-allowed;
  }
`;

const PreviewImageWithFallback: React.FC<{ src: string; alt: string }> = ({
  src,
  alt,
}) => {
  const { imageUrl } = useImageWithFallback(src);
  return <PreviewImage src={imageUrl} alt={alt} />;
};

interface FilePreviewProps {
  file: FileUploadState | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function FilePreview({
  file,
  isOpen,
  onClose,
}: FilePreviewProps) {

  const renderPreviewContent = () => {
    if (!file) return null;

    const fileName = file.file.name;
    const isImageFile =
      file.file.type.startsWith('image/') ||
      fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isPdfFile =
      file.file.type === 'application/pdf' ||
      fileName.toLowerCase().endsWith('.pdf');
    const isVideoFile =
      file.file.type.startsWith('video/') ||
      fileName.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
    const isSpreadsheetFile = fileName.match(/\.(xlsx|xls|csv)$/i);

    // Show error state
    if (file.status === 'error') {
      return (
        <>
          <PreviewCloseButton onClick={onClose}>
            <X size={20} />
          </PreviewCloseButton>
          <PreviewContainer>
            <PreviewFileName>{fileName}</PreviewFileName>
            <PreviewDocument>
              <DocumentIcon>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  color="#f04438"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                <DocumentText style={{ color: '#f04438' }}>
                  Upload failed: {file.error}
                </DocumentText>
              </DocumentIcon>
            </PreviewDocument>
          </PreviewContainer>
        </>
      );
    }

    // For images
    if (isImageFile) {
      return (
        <>
          <PreviewCloseButton onClick={onClose}>
            <X size={20} />
          </PreviewCloseButton>
          <PreviewContainer>
            <PreviewFileName>{fileName}</PreviewFileName>
            {file.preview || file.previewUrl ? (
              <PreviewImageWithFallback
                src={file.preview || file.previewUrl || ''}
                alt={fileName}
              />
            ) : (
              <PreviewDocument>
                <DocumentIcon>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <DocumentText>Image preview loading...</DocumentText>
                </DocumentIcon>
              </PreviewDocument>
            )}
          </PreviewContainer>
        </>
      );
    }

    // For video files
    if (isVideoFile) {
      // Priority: previewUrl (from server) > preview (blob URL) > create new blob (only if file has content)
      let videoUrl = file.previewUrl || file.preview;
      
      // Only create blob URL if file has actual content (size > 0)
      if (!videoUrl && file.file.size > 0) {
        videoUrl = URL.createObjectURL(file.file);
      }
      
      console.log('Video URL:', videoUrl, 'File size:', file.file.size, 'PreviewUrl:', file.previewUrl, 'Preview:', file.preview, file);

      if (!videoUrl) {
        return (
          <>
            <PreviewCloseButton onClick={onClose}>
              <X size={20} />
            </PreviewCloseButton>
            <PreviewContainer>
              <PreviewFileName>{fileName}</PreviewFileName>
              <PreviewDocument>
                <DocumentIcon>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <DocumentText>Video preview not available</DocumentText>
                </DocumentIcon>
              </PreviewDocument>
            </PreviewContainer>
          </>
        );
      }

      return (
        <>
          <PreviewCloseButton onClick={onClose}>
            <X size={20} />
          </PreviewCloseButton>
          <PreviewContainer>
            <PreviewFileName>{fileName}</PreviewFileName>
            <VideoPreview 
              controls 
              key={videoUrl}
              src={videoUrl}
            >
              Your browser does not support the video tag.
            </VideoPreview>
          </PreviewContainer>
        </>
      );
    }

    // For spreadsheet files (Excel, CSV)
    if (isSpreadsheetFile) {
      const fileUrl = file.previewUrl || URL.createObjectURL(file.file);

      return (
        <>
          <PreviewCloseButton onClick={onClose}>
            <X size={20} />
          </PreviewCloseButton>
          <PreviewContainer>
            <PreviewFileName>{fileName}</PreviewFileName>
            <PreviewDocument>
              <DocumentIcon>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
                <DocumentText>Spreadsheet Document</DocumentText>
                <ViewFileButton
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  View File
                </ViewFileButton>
              </DocumentIcon>
            </PreviewDocument>
          </PreviewContainer>
        </>
      );
    }

    // For PDF files
    if (isPdfFile) {
      // Use previewUrl for existing attachments, or create object URL for new files
      const pdfUrl = file.previewUrl || URL.createObjectURL(file.file);

      return (
        <>
          <PreviewCloseButton onClick={onClose}>
            <X size={20} />
          </PreviewCloseButton>
          <PreviewContainer>
            <PreviewFileName>{fileName}</PreviewFileName>
            <PDFPreview src={pdfUrl} title={fileName} />
          </PreviewContainer>
        </>
      );
    }

    // For other document types
    return (
      <>
        <PreviewCloseButton onClick={onClose}>
          <X size={20} />
        </PreviewCloseButton>
        <PreviewContainer>
          <PreviewFileName>{file.file.name}</PreviewFileName>
          <PreviewDocument>
            <DocumentIcon>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
              <DocumentText>
                {file.file.type.includes('pdf') ? 'PDF Document' : 'Document'}
              </DocumentText>
              <DocumentText>
                Size: {(file.file.size / 1024 / 1024).toFixed(2)} MB
              </DocumentText>
            </DocumentIcon>
          </PreviewDocument>
        </PreviewContainer>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      {renderPreviewContent()}
    </Modal>
  );
}
